import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL != null ? process.env.REACT_APP_API_URL : 'http://localhost:8000';

const EXCLUSION_REASONS = {
  not_tracked: 'Nicht vollständig getrackt',
  cheat_day: 'Cheat Day / mehr gegessen',
  sick: 'Krank',
  estimated: 'Geschätzte Werte',
  other: 'Sonstiges'
};

function DataQuality() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArea, setSelectedArea] = useState('all');
  const [calculatingTrends, setCalculatingTrends] = useState(false);
  const [trendsMessage, setTrendsMessage] = useState(null);
  const [outliers, setOutliers] = useState(null);
  const [excludingDay, setExcludingDay] = useState(null);
  const [verifyingDay, setVerifyingDay] = useState(null);
  const [timePeriod, setTimePeriod] = useState(90);
  const [importingCronometer, setImportingCronometer] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selectedOutliers, setSelectedOutliers] = useState(new Set());
  const [batchExcluding, setBatchExcluding] = useState(false);
  const [batchReason, setBatchReason] = useState('other');
  const [interpolating, setInterpolating] = useState(false);
  const [interpolateMessage, setInterpolateMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    setSelectedOutliers(new Set());
    fetchReport();
    fetchOutliers();
  }, [timePeriod]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (timePeriod > 0) {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - timePeriod);
        params.set('start_date', start.toISOString().split('T')[0]);
        params.set('end_date', end.toISOString().split('T')[0]);
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API_URL}/api/data-quality/report${query}`);
      setReport(response.data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden des Reports: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      setSettings(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Settings:', err);
    }
  };

  const calculateTrends = async () => {
    try {
      setCalculatingTrends(true);
      setTrendsMessage(null);
      const response = await axios.post(`${API_URL}/api/data/calculate-trends?weeks=12`);
      setTrendsMessage({
        type: 'success',
        text: `${response.data.trends_calculated} wöchentliche Trends berechnet`
      });
    } catch (err) {
      setTrendsMessage({
        type: 'error',
        text: 'Fehler bei Trend-Berechnung: ' + err.message
      });
    } finally {
      setCalculatingTrends(false);
    }
  };

  const fetchOutliers = async () => {
    try {
      const days = timePeriod > 0 ? timePeriod : 9999;
      const response = await axios.get(`${API_URL}/api/data/outliers?days=${days}`);
      setOutliers(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Ausreißer:', err);
    }
  };

  const triggerCronometerImport = async () => {
    try {
      setImportingCronometer(true);
      setImportMessage(null);
      const response = await axios.post(`${API_URL}/api/import/cronometer?force=false`);
      const data = response.data;
      const parts = [];
      if (data.total_imported > 0) parts.push(`${data.total_imported} importiert`);
      if (data.total_updated > 0) parts.push(`${data.total_updated} aktualisiert`);
      if (data.total_skipped > 0) parts.push(`${data.total_skipped} übersprungen`);
      if (parts.length === 0) parts.push('Keine neuen Daten');
      setImportMessage({
        type: data.success ? 'success' : 'error',
        text: parts.join(', ')
      });
      // Refresh data after successful import
      if (data.success) {
        await Promise.all([fetchReport(), fetchOutliers()]);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setImportMessage({
        type: 'error',
        text: 'Import fehlgeschlagen: ' + detail
      });
    } finally {
      setImportingCronometer(false);
    }
  };

  const toggleExclusion = async (dateStr, currentlyExcluded, reason = null) => {
    try {
      setExcludingDay(dateStr);
      const params = new URLSearchParams({
        target_date: dateStr,
        exclude: (!currentlyExcluded).toString()
      });
      if (reason && !currentlyExcluded) {
        params.append('reason', reason);
      }
      await axios.post(`${API_URL}/api/data/exclude-day?${params.toString()}`);
      // Refresh data
      await Promise.all([fetchReport(), fetchOutliers()]);
    } catch (err) {
      alert('Fehler: ' + err.message);
    } finally {
      setExcludingDay(null);
    }
  };

  const verifyOutlier = async (dateStr) => {
    try {
      setVerifyingDay(dateStr);
      await axios.post(`${API_URL}/api/data/verify-outlier?target_date=${dateStr}&verified=true`);
      // Refresh outliers list
      await fetchOutliers();
    } catch (err) {
      alert('Fehler: ' + err.message);
    } finally {
      setVerifyingDay(null);
    }
  };

  const interpolateWeight = async () => {
    try {
      setInterpolating(true);
      setInterpolateMessage(null);
      const response = await axios.post(`${API_URL}/api/data/interpolate`, { fields: ['weight_kg'] });
      const data = response.data;
      setInterpolateMessage({
        type: data.interpolated_days > 0 ? 'success' : 'info',
        text: data.interpolated_days > 0
          ? `${data.interpolated_days} Tage interpoliert`
          : 'Keine Lücken gefunden (mind. 2 Messpunkte nötig)'
      });
      if (data.interpolated_days > 0) await fetchReport();
    } catch (err) {
      setInterpolateMessage({ type: 'error', text: 'Fehler: ' + err.message });
    } finally {
      setInterpolating(false);
    }
  };

  const batchExclude = async () => {
    if (selectedOutliers.size === 0) return;
    setBatchExcluding(true);
    try {
      await axios.post(`${API_URL}/api/data/batch-exclude?user_id=1`, {
        dates: Array.from(selectedOutliers),
        exclude: true,
        reason: batchReason
      });
      setSelectedOutliers(new Set());
      await Promise.all([fetchReport(), fetchOutliers()]);
    } catch (err) {
      console.error('Fehler beim Batch-Ausschließen:', err);
    } finally {
      setBatchExcluding(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner"></div>Lade Datenqualitäts-Report...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!report) return null;

  // Filter days based on selected area
  const getFilteredDays = () => {
    if (selectedArea === 'all') return report.days;
    return report.days.filter(day => {
      if (selectedArea === 'nutrition') return !day.has_nutrition;
      if (selectedArea === 'activity') return !day.has_activity;
      if (selectedArea === 'weight') return !day.has_weight;
      if (selectedArea === 'interpolated') return day.is_interpolated;
      if (selectedArea === 'provisional') return day.is_provisional;
      if (selectedArea === 'excluded') return day.exclude_from_trends;
      return true;
    });
  };

  const filteredDays = getFilteredDays();

  // Group gaps by area
  const gapsByArea = {
    nutrition: report.gaps.filter(g => g.area === 'nutrition'),
    activity: report.gaps.filter(g => g.area === 'activity'),
    weight: report.gaps.filter(g => g.area === 'weight'),
  };

  return (
    <div className="data-quality">
      <div className="page-header">
        <h2>Datenqualität & Vollständigkeit</h2>
        <select
          value={timePeriod}
          onChange={(e) => setTimePeriod(Number(e.target.value))}
          className="time-period-select"
        >
          <option value={30}>Letzte 30 Tage</option>
          <option value={90}>Letzte 90 Tage</option>
          <option value={180}>Letzte 180 Tage</option>
          <option value={365}>Letztes Jahr</option>
          <option value={0}>Alle Daten</option>
        </select>
      </div>

      {/* Coverage Summary */}
      <div className="coverage-summary">
        <h3>Abdeckung ({report.date_range.start} bis {report.date_range.end})</h3>
        <p>{report.total_days} Tage analysiert</p>

        <div className="coverage-bars">
          <div className="coverage-item">
            <label>Ernährung (Cronometer)</label>
            <div className="progress-bar">
              <div
                className="progress-fill nutrition"
                style={{ width: `${report.coverage.nutrition}%` }}
              />
            </div>
            <span>{report.coverage.nutrition}%</span>
          </div>

          <div className="coverage-item">
            <label>Aktivität (Garmin)</label>
            <div className="progress-bar">
              <div
                className="progress-fill activity"
                style={{ width: `${report.coverage.activity}%` }}
              />
            </div>
            <span>{report.coverage.activity}%</span>
          </div>

          <div className="coverage-item">
            <label>Gewicht</label>
            <div className="progress-bar">
              <div
                className="progress-fill weight"
                style={{ width: `${report.coverage.weight}%` }}
              />
            </div>
            <span>{report.coverage.weight}%</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="actions-section">
        <h3>Aktionen</h3>
        <button
          onClick={calculateTrends}
          disabled={calculatingTrends}
          className="action-button"
        >
          {calculatingTrends ? 'Berechne...' : 'Wöchentliche Trends berechnen'}
        </button>
        {trendsMessage && (
          <span className={`message ${trendsMessage.type}`}>
            {trendsMessage.text}
          </span>
        )}
        {settings?.cronometer_credentials_configured && (
          <>
            <button
              onClick={triggerCronometerImport}
              disabled={importingCronometer}
              className="action-button cronometer-btn"
            >
              {importingCronometer ? 'Importiere...' : 'Cronometer Import'}
            </button>
          </>
        )}
        {importMessage && (
          <span className={`message ${importMessage.type}`}>
            {importMessage.text}
          </span>
        )}
      </div>

      {/* Outliers / Verdächtige Tage */}
      {outliers && outliers.outliers.length > 0 && (
        <div className="outliers-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>Verdächtige Tage ({outliers.outliers.length})</h3>
            <select
              value={timePeriod}
              onChange={e => { setTimePeriod(Number(e.target.value)); }}
              className="time-period-select"
              style={{ fontSize: '0.85rem' }}
            >
              <option value={30}>30 Tage</option>
              <option value={90}>90 Tage</option>
              <option value={180}>180 Tage</option>
              <option value={365}>365 Tage</option>
              <option value={0}>Alle</option>
            </select>
          </div>
          <p className="outliers-info">
            Diese Tage weichen vom Durchschnitt ab (Ø {outliers.statistics.avg_calories} kcal).
            Klicke auf "Ausschließen" um sie von den Trend-Berechnungen auszunehmen.
          </p>
          <div className="outliers-list">
            {outliers.outliers.filter(o => !o.already_excluded).map((outlier, idx) => (
              <div key={idx} className={`outlier-item ${outlier.reason}`}>
                <input
                  type="checkbox"
                  checked={selectedOutliers.has(outlier.date)}
                  onChange={e => {
                    setSelectedOutliers(prev => {
                      const next = new Set(prev);
                      e.target.checked ? next.add(outlier.date) : next.delete(outlier.date);
                      return next;
                    });
                  }}
                  style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                />
                <div className="outlier-date">{outlier.date}</div>
                <div className="outlier-details">
                  <span className="outlier-reason-text">{outlier.details}</span>
                  {outlier.calories_consumed && (
                    <span className="outlier-calories">
                      {outlier.calories_consumed} kcal gegessen
                      {outlier.deficit && ` · Defizit: ${outlier.deficit} kcal`}
                    </span>
                  )}
                </div>
                <div className="outlier-actions">
                  <button
                    onClick={() => verifyOutlier(outlier.date)}
                    disabled={verifyingDay === outlier.date}
                    className="verify-btn"
                    title="Bestätigen, dass die Daten korrekt sind"
                  >
                    {verifyingDay === outlier.date ? '...' : '✓ Ist korrekt'}
                  </button>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        toggleExclusion(outlier.date, false, e.target.value);
                      }
                    }}
                    disabled={excludingDay === outlier.date}
                    defaultValue=""
                  >
                    <option value="" disabled>Ausschließen...</option>
                    {Object.entries(EXCLUSION_REASONS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
            {selectedOutliers.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
                <select
                  value={batchReason}
                  onChange={e => setBatchReason(e.target.value)}
                  className="time-period-select"
                  style={{ fontSize: '0.85rem' }}
                >
                  {Object.entries(EXCLUSION_REASONS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <button
                  onClick={batchExclude}
                  disabled={batchExcluding}
                  className="btn-danger"
                >
                  {batchExcluding ? 'Wird ausgeschlossen...' : `${selectedOutliers.size} Tage ausschließen`}
                </button>
              </div>
            )}

          {/* Show already excluded days */}
          {outliers.statistics.days_excluded > 0 && (
            <div className="excluded-summary">
              <h4>Bereits ausgeschlossen ({outliers.statistics.days_excluded} Tage)</h4>
              <div className="excluded-list">
                {report.days.filter(d => d.exclude_from_trends).slice(0, 5).map((day, idx) => (
                  <div key={idx} className="excluded-item">
                    <span>{day.date}</span>
                    <span className="exclusion-reason">{EXCLUSION_REASONS[day.exclusion_reason] || day.exclusion_reason}</span>
                    <button
                      onClick={() => toggleExclusion(day.date, true)}
                      disabled={excludingDay === day.date}
                      className="include-btn"
                    >
                      Wieder einschließen
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Data Gaps */}
      <div className="data-gaps">
        <h3>Datenlücken</h3>

        {['nutrition', 'activity', 'weight'].map(area => (
          <div key={area} className="gap-section">
            <div className="gap-header">
              <h4>
                {area === 'nutrition' && 'Ernährung'}
                {area === 'activity' && 'Aktivität'}
                {area === 'weight' && 'Gewicht'}
                {gapsByArea[area].length > 0 && (
                  <span className="gap-count"> ({gapsByArea[area].length} Lücken)</span>
                )}
              </h4>
              {area === 'weight' && gapsByArea.weight.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={interpolateWeight}
                    disabled={interpolating}
                    className="action-button interpolate-btn"
                  >
                    {interpolating ? 'Interpoliere...' : 'Lücken interpolieren'}
                  </button>
                  {interpolateMessage && (
                    <span className={`message ${interpolateMessage.type}`}>
                      {interpolateMessage.text}
                    </span>
                  )}
                </div>
              )}
            </div>
            {gapsByArea[area].length === 0 ? (
              <p className="no-gaps">Keine Lücken</p>
            ) : (
              <ul className="gap-list">
                {gapsByArea[area].slice(0, 5).map((gap, idx) => (
                  <li key={idx}>
                    {gap.start_date === gap.end_date ? (
                      <span>{gap.start_date}</span>
                    ) : (
                      <span>{gap.start_date} bis {gap.end_date}</span>
                    )}
                    <span className="gap-days">({gap.days_missing} Tage)</span>
                  </li>
                ))}
                {gapsByArea[area].length > 5 && (
                  <li className="more">...und {gapsByArea[area].length - 5} weitere</li>
                )}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Recent Imports */}
      <div className="recent-imports">
        <h3>Letzte Imports</h3>
        {report.recent_imports.length === 0 ? (
          <p>Keine Imports gefunden</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Quelle</th>
                <th>Neu</th>
                <th>Aktualisiert</th>
                <th>Übersprungen</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {report.recent_imports.map((imp, idx) => (
                <tr key={idx} className={imp.status === 'completed' ? 'success' : 'warning'}>
                  <td>{imp.date ? new Date(imp.date).toLocaleDateString('de-DE') : '-'}</td>
                  <td>{imp.source}</td>
                  <td className="number">{imp.records_new}</td>
                  <td className="number">{imp.records_updated}</td>
                  <td className={imp.records_skipped > 0 ? 'number warning' : 'number'}>
                    {imp.records_skipped}
                  </td>
                  <td>
                    <span className={`status ${imp.status}`}>{imp.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Day-by-Day View */}
      <div className="daily-view">
        <h3>
          Tagesübersicht
          <select
            value={selectedArea}
            onChange={(e) => setSelectedArea(e.target.value)}
            className="area-select"
          >
            <option value="all">Alle Tage</option>
            <option value="nutrition">Ohne Ernährung</option>
            <option value="activity">Ohne Aktivität</option>
            <option value="weight">Ohne Gewicht</option>
            <option value="interpolated">Nur Interpoliert</option>
            <option value="provisional">Nur Provisorisch</option>
            <option value="excluded">Nur Ausgeschlossen</option>
          </select>
        </h3>

        <div className="legend">
          <span><strong>N</strong>=Ernährung <strong>A</strong>=Aktivität <strong>G</strong>=Gewicht</span>
          <span>🟢 Importiert</span>
          <span>🟡 Interpoliert</span>
          <span>⚪ Provisorisch</span>
          <span>🔴 Ausgeschlossen</span>
        </div>

        <div className="calendar-grid">
          {filteredDays.slice().reverse().map((day, idx) => (
            <div
              key={idx}
              className={`day-cell ${day.has_nutrition ? 'has-nutrition' : ''} ${day.has_activity ? 'has-activity' : ''} ${day.has_weight ? 'has-weight' : ''} ${day.is_interpolated ? 'interpolated' : ''} ${day.is_provisional ? 'provisional' : ''} ${day.exclude_from_trends ? 'excluded' : ''}`}
              title={`${day.date}\nKalorien: ${day.calories_in || '-'}\nVerbrannt: ${day.calories_out || '-'}\nGewicht: ${day.weight || '-'}${day.is_interpolated ? '\n⚠️ Interpoliert' : ''}${day.is_provisional ? '\n⏳ Provisorisch' : ''}${day.exclude_from_trends ? '\n🚫 Ausgeschlossen: ' + (EXCLUSION_REASONS[day.exclusion_reason] || day.exclusion_reason) : ''}`}
              onClick={() => {
                if (day.exclude_from_trends) {
                  if (window.confirm(`Tag ${day.date} wieder in Trends einschließen?`)) {
                    toggleExclusion(day.date, true);
                  }
                }
              }}
            >
              <span className="day-date">{day.date.slice(5)}</span>
              {day.is_interpolated && <span className="badge interpolated" title="Interpoliert">~</span>}
              {day.is_provisional && <span className="badge provisional" title="Provisorisch">?</span>}
              {day.exclude_from_trends && <span className="badge excluded" title={EXCLUSION_REASONS[day.exclusion_reason] || 'Ausgeschlossen'}>X</span>}
              <div className="indicators">
                <span className={`indicator ${day.has_nutrition ? 'filled' : 'empty'}`} title="Ernährung">N</span>
                <span className={`indicator ${day.has_activity ? 'filled' : 'empty'}`} title="Aktivität">A</span>
                <span className={`indicator ${day.has_weight ? 'filled' : 'empty'} ${day.is_interpolated && day.has_weight ? 'interpolated' : ''}`} title="Gewicht">G</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .data-quality {
          padding: var(--space-4, 1rem);
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-6, 1.5rem);
          flex-wrap: wrap;
          gap: var(--space-2, 0.5rem);
        }

        h2 {
          margin-bottom: 0;
          color: var(--text-primary, #1f2937);
          font-weight: 700;
        }

        .time-period-select {
          padding: var(--space-2, 0.5rem) var(--space-3, 0.75rem);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-md, 8px);
          background: var(--card-bg, white);
          color: var(--text-primary, #1f2937);
          font-size: 0.9rem;
          cursor: pointer;
        }

        h3 {
          margin: var(--space-6, 1.5rem) 0 var(--space-4, 1rem);
          padding-bottom: var(--space-2, 0.5rem);
          border-bottom: 2px solid var(--border-color, #e5e7eb);
          color: var(--text-primary, #1f2937);
        }

        .area-select {
          margin-left: var(--space-4, 1rem);
          padding: var(--space-2, 0.5rem);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-md, 8px);
          background: var(--card-bg, white);
          color: var(--text-primary, #1f2937);
        }

        .legend {
          margin-bottom: var(--space-4, 1rem);
          font-size: 0.85rem;
          color: var(--text-secondary, #6b7280);
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-4, 1rem);
        }

        .coverage-summary {
          background: var(--card-bg, white);
          padding: var(--space-6, 1.5rem);
          border-radius: var(--radius-lg, 12px);
          margin-bottom: var(--space-6, 1.5rem);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
        }

        .coverage-bars {
          display: flex;
          flex-direction: column;
          gap: var(--space-4, 1rem);
        }

        .coverage-item {
          display: flex;
          align-items: center;
          gap: var(--space-4, 1rem);
        }

        .coverage-item label {
          width: 180px;
          font-weight: 500;
          color: var(--text-primary, #1f2937);
        }

        .progress-bar {
          flex: 1;
          height: 24px;
          background: var(--bg-color, #f9fafb);
          border-radius: var(--radius-md, 8px);
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          transition: width 0.3s;
          border-radius: var(--radius-md, 8px);
        }

        .progress-fill.nutrition { background: var(--success-500, #22c55e); }
        .progress-fill.activity { background: var(--primary-500, #3b82f6); }
        .progress-fill.weight { background: var(--warning-500, #f59e0b); }

        .coverage-item span {
          width: 50px;
          text-align: right;
          font-weight: bold;
          color: var(--text-primary, #1f2937);
        }

        .data-gaps {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: var(--space-4, 1rem);
          margin-bottom: var(--space-6, 1.5rem);
        }

        .gap-section {
          background: var(--card-bg, white);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-4, 1rem);
        }

        .gap-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: var(--space-2, 0.5rem);
          margin-bottom: var(--space-2, 0.5rem);
        }

        .gap-section h4 {
          margin: 0;
          font-size: 0.95rem;
          color: var(--text-primary, #1f2937);
        }

        .interpolate-btn {
          font-size: 0.8rem;
          padding: var(--space-1, 0.25rem) var(--space-3, 0.75rem);
          background: var(--primary-500, #3b82f6);
        }

        .interpolate-btn:hover:not(:disabled) {
          background: var(--primary-600, #2563eb);
        }

        .gap-count {
          color: var(--error-500, #ef4444);
          font-weight: normal;
        }

        .no-gaps {
          color: var(--success-600, #16a34a);
          font-style: italic;
        }

        .gap-list {
          list-style: none;
          padding: 0;
          margin: 0;
          font-size: 0.9rem;
        }

        .gap-list li {
          padding: var(--space-1, 0.25rem) 0;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          color: var(--text-primary, #1f2937);
        }

        .gap-days {
          color: var(--text-secondary, #6b7280);
          margin-left: var(--space-2, 0.5rem);
        }

        .more {
          color: var(--text-secondary, #6b7280);
          font-style: italic;
        }

        .recent-imports {
          background: var(--card-bg, white);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-4, 1rem);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
          overflow-x: auto;
        }

        .recent-imports table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
        }

        .recent-imports th,
        .recent-imports td {
          padding: var(--space-2, 0.5rem);
          text-align: left;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }

        .recent-imports th {
          background: var(--bg-color, #f9fafb);
          font-weight: 600;
          color: var(--text-muted, #9ca3af);
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
        }

        .recent-imports td {
          color: var(--text-primary, #1f2937);
        }

        .recent-imports .number {
          text-align: right;
        }

        .recent-imports .warning {
          color: var(--error-500, #ef4444);
        }

        .status {
          padding: var(--space-1, 0.25rem) var(--space-2, 0.5rem);
          border-radius: var(--radius-sm, 6px);
          font-size: 0.8rem;
        }

        .status.completed {
          background: var(--success-50, #f0fdf4);
          color: var(--success-700, #15803d);
        }

        .status.failed {
          background: var(--error-50, #fef2f2);
          color: var(--error-700, #b91c1c);
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
          gap: 4px;
          margin-top: var(--space-4, 1rem);
        }

        .day-cell {
          background: var(--card-bg, white);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-sm, 6px);
          padding: var(--space-2, 0.5rem);
          font-size: 0.8rem;
          text-align: center;
          position: relative;
          transition: transform 0.15s;
        }

        .day-cell:hover {
          transform: scale(1.02);
        }

        .day-cell.excluded {
          cursor: pointer;
        }

        .day-date {
          display: block;
          font-weight: 500;
          margin-bottom: var(--space-1, 0.25rem);
          color: var(--text-primary, #1f2937);
        }

        .indicators {
          display: flex;
          justify-content: center;
          gap: 2px;
        }

        .indicator {
          width: 18px;
          height: 18px;
          border-radius: 3px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          font-weight: bold;
        }

        .indicator.filled {
          background: var(--success-500, #22c55e);
          color: white;
        }

        .indicator.empty {
          background: var(--bg-color, #f9fafb);
          color: var(--text-muted, #9ca3af);
        }

        .indicator.interpolated {
          background: var(--warning-500, #f59e0b);
          color: white;
        }

        .day-cell.interpolated {
          border-color: var(--warning-500, #f59e0b);
          border-width: 2px;
        }

        .day-cell.provisional {
          border-color: var(--text-secondary, #6b7280);
          border-style: dashed;
          opacity: 0.7;
        }

        .badge {
          position: absolute;
          top: 2px;
          right: 2px;
          font-size: 0.6rem;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }

        .badge.interpolated {
          background: var(--warning-500, #f59e0b);
          color: white;
        }

        .badge.provisional {
          background: var(--text-secondary, #6b7280);
          color: white;
        }

        .badge.excluded {
          background: var(--error-500, #ef4444);
          color: white;
        }

        .loading, .error {
          padding: var(--space-8, 2rem);
          text-align: center;
        }

        .error {
          color: var(--error-500, #ef4444);
        }

        .actions-section {
          background: var(--card-bg, white);
          padding: var(--space-4, 1rem) var(--space-6, 1.5rem);
          border-radius: var(--radius-lg, 12px);
          margin-bottom: var(--space-6, 1.5rem);
          display: flex;
          align-items: center;
          gap: var(--space-4, 1rem);
          flex-wrap: wrap;
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
        }

        .actions-section h3 {
          margin: 0;
          padding: 0;
          border: none;
          font-size: 1rem;
        }

        .action-button {
          background: var(--primary-600, #2563eb);
          color: white;
          border: none;
          padding: var(--space-2, 0.5rem) var(--space-4, 1rem);
          border-radius: var(--radius-md, 8px);
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: background 0.2s;
        }

        .action-button:hover:not(:disabled) {
          background: var(--primary-700, #1d4ed8);
        }

        .action-button:disabled {
          background: var(--text-muted, #9ca3af);
          cursor: not-allowed;
        }

        .action-button.cronometer-btn {
          background: var(--success-600, #16a34a);
        }

        .action-button.cronometer-btn:hover:not(:disabled) {
          background: var(--success-700, #15803d);
        }

        .message {
          font-size: 0.9rem;
          padding: var(--space-1, 0.25rem) var(--space-2, 0.5rem);
          border-radius: var(--radius-sm, 6px);
        }

        .message.success {
          background: var(--success-50, #f0fdf4);
          color: var(--success-700, #15803d);
        }

        .message.error {
          background: var(--error-50, #fef2f2);
          color: var(--error-700, #b91c1c);
        }

        /* Outliers Section */
        .outliers-section {
          background: var(--warning-50, #fffbeb);
          border: 1px solid var(--warning-500, #f59e0b);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-6, 1.5rem);
          margin-bottom: var(--space-6, 1.5rem);
        }

        .outliers-section h3 {
          margin: 0 0 var(--space-2, 0.5rem);
          padding: 0;
          border: none;
          color: var(--warning-700, #b45309);
        }

        .outliers-info {
          font-size: 0.9rem;
          color: var(--warning-700, #b45309);
          margin-bottom: var(--space-4, 1rem);
        }

        .outliers-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-2, 0.5rem);
        }

        .outlier-item {
          display: flex;
          align-items: center;
          gap: var(--space-4, 1rem);
          background: var(--card-bg, white);
          padding: var(--space-3, 0.75rem);
          border-radius: var(--radius-md, 8px);
          border-left: 4px solid var(--warning-500, #f59e0b);
        }

        .outlier-item.low_calories {
          border-left-color: var(--error-500, #ef4444);
        }

        .outlier-item.high_calories {
          border-left-color: var(--warning-600, #d97706);
        }

        .outlier-item.high_deficit {
          border-left-color: #6f42c1;
        }

        .outlier-date {
          font-weight: 600;
          min-width: 100px;
          color: var(--text-primary, #1f2937);
        }

        .outlier-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: var(--space-1, 0.25rem);
        }

        .outlier-reason-text {
          font-size: 0.9rem;
          color: var(--text-primary, #1f2937);
        }

        .outlier-calories {
          font-size: 0.8rem;
          color: var(--text-secondary, #6b7280);
        }

        .outlier-actions {
          display: flex;
          gap: var(--space-2, 0.5rem);
          align-items: center;
        }

        .outlier-actions select {
          padding: var(--space-1, 0.25rem) var(--space-2, 0.5rem);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-sm, 6px);
          font-size: 0.85rem;
          background: var(--card-bg, white);
          color: var(--text-primary, #1f2937);
        }

        .verify-btn {
          background: var(--success-500, #22c55e);
          color: white;
          border: none;
          padding: var(--space-1, 0.25rem) var(--space-2, 0.5rem);
          border-radius: var(--radius-sm, 6px);
          cursor: pointer;
          font-size: 0.85rem;
          white-space: nowrap;
          font-weight: 500;
        }

        .verify-btn:hover:not(:disabled) {
          background: var(--success-600, #16a34a);
        }

        .verify-btn:disabled {
          background: var(--text-muted, #9ca3af);
          cursor: not-allowed;
        }

        .more-outliers {
          color: var(--warning-700, #b45309);
          font-style: italic;
          margin-top: var(--space-2, 0.5rem);
        }

        .excluded-summary {
          margin-top: var(--space-4, 1rem);
          padding-top: var(--space-4, 1rem);
          border-top: 1px solid var(--warning-500, #f59e0b);
        }

        .excluded-summary h4 {
          margin: 0 0 var(--space-2, 0.5rem);
          font-size: 0.95rem;
          color: var(--warning-700, #b45309);
        }

        .excluded-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-1, 0.25rem);
        }

        .excluded-item {
          display: flex;
          align-items: center;
          gap: var(--space-4, 1rem);
          font-size: 0.9rem;
          padding: var(--space-1, 0.25rem) 0;
          color: var(--text-primary, #1f2937);
        }

        .exclusion-reason {
          color: var(--text-secondary, #6b7280);
          flex: 1;
        }

        .include-btn {
          background: none;
          border: 1px solid var(--success-500, #22c55e);
          color: var(--success-500, #22c55e);
          padding: var(--space-1, 0.25rem) var(--space-2, 0.5rem);
          border-radius: var(--radius-sm, 6px);
          cursor: pointer;
          font-size: 0.8rem;
          transition: all 0.2s;
        }

        .include-btn:hover {
          background: var(--success-500, #22c55e);
          color: white;
        }

        .btn-danger {
          background: var(--error-500, #ef4444);
          color: white;
          border: none;
          border-radius: var(--radius-md, 8px);
          padding: 0.5rem 1rem;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
        }
        .btn-danger:hover {
          background: var(--error-600, #dc2626);
        }
        .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Excluded day cell */
        .day-cell.excluded {
          border-color: var(--error-500, #ef4444);
          border-width: 2px;
          opacity: 0.6;
          background: var(--error-50, #fef2f2);
        }

        @media (max-width: 768px) {
          .outlier-item {
            flex-direction: column;
            align-items: flex-start;
          }

          .outlier-actions {
            width: 100%;
            justify-content: flex-end;
          }

          .coverage-item {
            flex-direction: column;
            align-items: flex-start;
          }

          .coverage-item label {
            width: auto;
          }

          .verify-btn {
            min-height: 44px;
            min-width: 44px;
          }
        }
      `}</style>
    </div>
  );
}

export default DataQuality;
